import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input,
    HStack, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, FormControl, FormLabel, Select, SimpleGrid, AlertDialog,
    AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent,
    AlertDialogOverlay, Spinner, Badge, Checkbox, Image, Stat, StatLabel, StatNumber,
    Textarea, Tooltip, InputGroup, InputLeftElement, ButtonGroup, Card, CardBody, VStack,
    Tag, useBreakpointValue, Link
} from "@chakra-ui/react";
import {
    CheckIcon, CloseIcon, DeleteIcon, ViewIcon, StarIcon, RepeatIcon, SearchIcon,
    TimeIcon, NotAllowedIcon, ExternalLinkIcon, DownloadIcon
} from "@chakra-ui/icons";
import { LayoutGrid, List as ListIcon } from "lucide-react";
import { RadioGroup, Radio, Stack as ChakraStack } from "@chakra-ui/react";
import {
    getImagens, getImagensContadores, getImagensFornecedores, aprovarImagem, rejeitarImagem,
    marcarImagemPendente, aprovarImagensMassa, rejeitarImagensMassa, excluirImagensMassa,
    definirImagemPrincipal, deletarImagem, restaurarImagem, bloquearImagem,
    excluirImagemPermanente, excluirImagensMassaPermanente,
    getImagensContadoresIa, analisarImagemIa, analisarImagensIaMassa, feedbackImagemIa,
    autoCorrigirImagemIa
} from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import ScrollToTop from "../components/ScrollToTop";
import { downloadImagem } from "../utils/downloadImagem";

const STATUS_COLORS = {
    pendente: "yellow",
    aprovada: "green",
    rejeitada: "red",
};

const ORIGEM_LABELS = {
    legado: "Legado",
    fornecedor: "Fornecedor",
    google: "Google",
    marketplace: "Marketplace",
    upload_manual: "Upload Manual",
    alternativo: "Alternativo",
};

const SORT_OPTIONS = [
    { value: "criado_desc",       label: "Mais recentes (criação)" },
    { value: "criado_asc",        label: "Mais antigas (criação)" },
    { value: "atualizado_desc",   label: "Atualizadas recentemente" },
    { value: "atualizado_asc",    label: "Atualizadas há mais tempo" },
    { value: "gravidade_ia_desc", label: "Gravidade IA (mais grave primeiro)" },
    { value: "confianca_ia_desc", label: "Confiança IA (mais alta primeiro)" },
];

const AI_STATUS_LABELS = {
    nao_analisada: "Não analisada",
    analisando:    "Analisando…",
    ok:            "OK",
    divergente:    "Divergente",
    erro:          "Erro",
};
const AI_STATUS_COLORS = {
    nao_analisada: "gray",
    analisando:    "blue",
    ok:            "green",
    divergente:    "orange",
    erro:          "red",
};
const AI_GRAVIDADE_LABELS = {
    critico: "Crítica",
    alto:    "Alta",
    medio:   "Média",
    baixo:   "Baixa",
    info:    "Info",
};
const AI_GRAVIDADE_COLORS = {
    critico: "red",
    alto:    "orange",
    medio:   "yellow",
    baixo:   "gray",
    info:    "blue",
};
const AI_TIPO_PROBLEMA_LABELS = {
    mismatch_total:    "Imagem não combina com o produto",
    kit_vs_unico:      "Imagem mostra kit, mas produto é peça única",
    unico_vs_kit:      "Imagem mostra peça única, mas produto é kit",
    marca_divergente:  "Marca/logo divergente",
    logo:              "Logo/marca dominante (ML pode rejeitar)",
    qualidade:         "Qualidade ruim / imagem genérica",
};

const initialFiltros = {
    q: "",
    status: "",
    origem: "",
    fornecedor_id: "",
    incluir_excluidas: false,
    apenas_excluidas: false,
    sort: "criado_desc",
    ai_analise_status: "",
    ai_gravidade: "",
};

const PURGA_DIAS = 30;
const diasParaPurga = (deletedAt) => {
    if (!deletedAt) return null;
    const diff = PURGA_DIAS - Math.floor((Date.now() - new Date(deletedAt)) / 86400000);
    return diff;
};

const AiBadge = ({ imagem, compact = false }) => {
    const status = imagem.ai_analise_status || "nao_analisada";
    if (status === "nao_analisada") {
        return (
            <Tooltip label="Não foi analisada pela IA" hasArrow>
                <Badge colorScheme="gray" variant="subtle" fontSize="9px">🤖 —</Badge>
            </Tooltip>
        );
    }
    if (status === "analisando") {
        return <Badge colorScheme="blue" variant="subtle" fontSize="9px">🤖 Analisando…</Badge>;
    }
    if (status === "erro") {
        return (
            <Tooltip label={imagem.ai_observacoes || "Erro na análise"} hasArrow>
                <Badge colorScheme="red" variant="subtle" fontSize="9px">🤖 Erro</Badge>
            </Tooltip>
        );
    }
    if (status === "ok") {
        const conf = imagem.ai_confianca != null ? `${Math.round(imagem.ai_confianca)}%` : "";
        return (
            <Tooltip label={imagem.ai_observacoes || "IA não detectou divergência"} hasArrow>
                <Badge colorScheme="green" variant="subtle" fontSize="9px">🤖 OK {conf}</Badge>
            </Tooltip>
        );
    }
    const grav = imagem.ai_gravidade || "medio";
    const conf = imagem.ai_confianca != null ? `${Math.round(imagem.ai_confianca)}%` : "";
    const tipoLabel = AI_TIPO_PROBLEMA_LABELS[imagem.ai_tipo_problema] || "Divergência";
    return (
        <Tooltip label={`${tipoLabel} — ${imagem.ai_observacoes || ""}`} hasArrow>
            <Badge colorScheme={AI_GRAVIDADE_COLORS[grav]} fontSize="9px">
                🤖 ⚠ {AI_GRAVIDADE_LABELS[grav]} {conf}
            </Badge>
        </Tooltip>
    );
};

const ProdutoInfo = ({ imagem, compact = false }) => (
    <VStack align="flex-start" spacing={0.5} fontSize={compact ? "xs" : "sm"} minW={0}>
        <HStack spacing={1.5}>
            <Badge colorScheme="gray" fontSize="xs" fontFamily="mono">
                #{imagem.produto_id}
            </Badge>
            <Tooltip label="Ver produto">
                <Link href={`/produtos?id=${imagem.produto_id}`} isExternal>
                    <ExternalLinkIcon boxSize={3} color="blue.400" />
                </Link>
            </Tooltip>
        </HStack>
        <Text fontWeight="semibold" noOfLines={2} title={imagem.produto_nome}>
            {imagem.produto_nome || `Produto #${imagem.produto_id}`}
        </Text>
        <HStack spacing={2} flexWrap="wrap" fontSize="xs" color="gray.600">
            {imagem.produto_codigo_global && (
                <Tag size="sm" variant="subtle" colorScheme="blue">
                    {imagem.produto_codigo_global}
                </Tag>
            )}
            {imagem.produto_marca && (
                <Tag size="sm" variant="subtle" colorScheme="purple">
                    {imagem.produto_marca}
                </Tag>
            )}
            {imagem.produto_fornecedor_nome && (
                <Tag size="sm" variant="subtle" colorScheme="teal">
                    {imagem.produto_fornecedor_nome}
                </Tag>
            )}
        </HStack>
        <HStack spacing={2} fontSize="xs" color="gray.500" flexWrap="wrap">
            {imagem.produto_codigo_fornecedor && (
                <Text>Forn: <b>{imagem.produto_codigo_fornecedor}</b></Text>
            )}
            {imagem.produto_codigo_fabricante && (
                <Text>Fabr: <b>{imagem.produto_codigo_fabricante}</b></Text>
            )}
        </HStack>
    </VStack>
);

const BancoImagens = () => {
    const toast = useToast();
    const cancelRef = useRef();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const location = useLocation();

    const [imagens, setImagens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationMeta, setPaginationMeta] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(30);
    const [filtros, setFiltros] = useState(() => {
        const isExcluidas = new URLSearchParams(location.search).get('excluidas') === '1';
        return { ...initialFiltros, apenas_excluidas: isExcluidas };
    });
    const [contadores, setContadores] = useState({ pendentes: 0, aprovadas: 0, rejeitadas: 0, excluidas: 0 });
    const [contadoresIa, setContadoresIa] = useState({
        por_status: { nao_analisada: 0, analisando: 0, ok: 0, divergente: 0, erro: 0 },
        por_gravidade: { critico: 0, alto: 0, medio: 0, baixo: 0, info: 0 },
    });
    const [fornecedores, setFornecedores] = useState([]);
    const [viewMode, setViewMode] = useState("table");
    const [analisandoIa, setAnalisandoIa] = useState(false);

    const debouncedBusca = useDebounce(filtros.q, 400);

    const [selecionadas, setSelecionadas] = useState(new Set());
    const [previewImagem, setPreviewImagem] = useState(null);

    const [confirmAlert, setConfirmAlert] = useState({ isOpen: false, type: "", id: null, ids: null });
    const [motivoRejeicao, setMotivoRejeicao] = useState("");
    const [excluirPermanente, setExcluirPermanente] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [destacarIds, setDestacarIds] = useState(new Set());
    const [modalBloqueio, setModalBloqueio] = useState({ isOpen: false, imagemId: null, produtoIdImg: null });
    const [bloqueioForm, setBloqueioForm] = useState({ escopo: "produto", motivo: "" });
    const [bloqueando, setBloqueando] = useState(false);

    const piscarItens = (ids) => {
        setDestacarIds(new Set(ids));
        setTimeout(() => setDestacarIds(new Set()), 1500);
    };

    const abrirModalBloqueio = (img) => {
        setBloqueioForm({ escopo: "produto", motivo: "" });
        setModalBloqueio({ isOpen: true, imagemId: img.id, produtoIdImg: img.produto_id });
    };
    const fecharModalBloqueio = () => setModalBloqueio({ isOpen: false, imagemId: null, produtoIdImg: null });

    const confirmarBloqueio = async () => {
        setBloqueando(true);
        try {
            const res = await bloquearImagem(modalBloqueio.imagemId, bloqueioForm.escopo, bloqueioForm.motivo || null);
            const copias = res.data?.["cópias_excluidas"] ?? 0;
            toast({
                title: "Imagem bloqueada",
                description: `Escopo: ${bloqueioForm.escopo === "global" ? "global" : "este produto"}${copias > 0 ? ` · ${copias} cópia(s) excluída(s)` : ""}`,
                status: "success", duration: 3500,
            });
            piscarItens([modalBloqueio.imagemId]);
            fecharModalBloqueio();
            fetchImagens({ silencioso: true });
        } catch (err) {
            toast({
                title: "Erro ao bloquear",
                description: err.response?.data?.error || err.message,
                status: "error", duration: 4000,
            });
        }
        setBloqueando(false);
    };

    useEffect(() => {
        if (isMobile) setViewMode("card");
    }, [isMobile]);

    const fetchImagens = useCallback(async (opts = {}) => {
        const silencioso = !!opts.silencioso;
        if (!silencioso) setLoading(true);
        const params = { page: currentPage, per_page: perPage, sort: filtros.sort };
        if (filtros.status) params.status = filtros.status;
        if (filtros.origem) params.origem = filtros.origem;
        if (filtros.fornecedor_id) params.fornecedor_id = filtros.fornecedor_id;
        if (debouncedBusca) params.q = debouncedBusca;
        if (filtros.apenas_excluidas)   params.apenas_excluidas  = 1;
        else if (filtros.incluir_excluidas) params.incluir_excluidas = 1;
        if (filtros.ai_analise_status) params.ai_analise_status = filtros.ai_analise_status;
        if (filtros.ai_gravidade)      params.ai_gravidade      = filtros.ai_gravidade;

        try {
            const res = await getImagens(params);
            setImagens(res.data.data || []);
            setPaginationMeta({
                current_page: res.data.current_page,
                last_page: res.data.last_page,
                total: res.data.total,
            });
            setSelecionadas(new Set());
        } catch (e) {
            toast({ title: "Erro ao carregar imagens", status: "error", duration: 3000 });
        }
        if (!silencioso) setLoading(false);
    }, [currentPage, perPage, filtros.sort, filtros.status, filtros.origem,
        filtros.fornecedor_id, filtros.incluir_excluidas, filtros.apenas_excluidas,
        filtros.ai_analise_status, filtros.ai_gravidade, debouncedBusca, toast]);

    const fetchContadores = useCallback(async () => {
        try {
            const res = await getImagensContadores();
            setContadores(res.data);
        } catch (e) {}
    }, []);

    const fetchContadoresIa = useCallback(async () => {
        try {
            const res = await getImagensContadoresIa();
            setContadoresIa(res.data);
        } catch (e) {}
    }, []);

    const fetchFornecedores = useCallback(async () => {
        try {
            const res = await getImagensFornecedores();
            setFornecedores(res.data || []);
        } catch (e) {}
    }, []);

    useEffect(() => { fetchImagens(); }, [fetchImagens]);
    useEffect(() => { fetchContadores(); }, [fetchContadores, imagens.length]);
    useEffect(() => { fetchContadoresIa(); }, [fetchContadoresIa, imagens.length]);
    useEffect(() => { fetchFornecedores(); }, [fetchFornecedores]);

    useEffect(() => {
        const isExcluidas = new URLSearchParams(location.search).get('excluidas') === '1';
        setFiltros(prev => ({ ...initialFiltros, apenas_excluidas: isExcluidas }));
        setCurrentPage(1);
    }, [location.search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filtros.status, filtros.origem, filtros.fornecedor_id, filtros.sort,
        filtros.incluir_excluidas, filtros.ai_analise_status, filtros.ai_gravidade, debouncedBusca]);

    const analisarIaIndividual = async (id) => {
        try {
            await analisarImagemIa(id);
            toast({ title: "Análise enfileirada", description: "A IA vai avaliar essa imagem em instantes.", status: "info", duration: 2500 });
            setTimeout(() => { fetchImagens({ silencioso: true }); fetchContadoresIa(); }, 1500);
        } catch (e) {
            toast({ title: "Erro ao enfileirar análise", description: e.response?.data?.error || e.message, status: "error", duration: 4000 });
        }
    };

    const autoCorrigirImagem = async (id) => {
        try {
            await autoCorrigirImagemIa(id);
            toast({
                title: "Auto-correção enfileirada",
                description: "A IA vai procurar uma imagem correta no Google. Pode levar alguns minutos.",
                status: "info", duration: 4000,
            });
            piscarItens([id]);
            setTimeout(() => { fetchImagens({ silencioso: true }); fetchContadoresIa(); }, 2000);
        } catch (e) {
            toast({
                title: "Erro ao enfileirar auto-correção",
                description: e.response?.data?.error || e.message,
                status: "error", duration: 4000,
            });
        }
    };

    const analisarIaSelecionadas = async () => {
        if (selecionadas.size === 0) return;
        setAnalisandoIa(true);
        try {
            const r = await analisarImagensIaMassa({ ids: Array.from(selecionadas), forcar: true });
            toast({ title: `${r.data.enfileiradas} imagens enfileiradas para análise da IA`, status: "success", duration: 3000 });
            setTimeout(() => { fetchImagens({ silencioso: true }); fetchContadoresIa(); }, 1500);
        } catch (e) {
            toast({ title: "Erro", description: e.response?.data?.error || e.message, status: "error", duration: 4000 });
        }
        setAnalisandoIa(false);
    };

    const enviarFeedbackIa = async (id, feedback) => {
        try {
            await feedbackImagemIa(id, feedback);
            toast({
                title: feedback === "concordou" ? "Marcado: IA acertou" : feedback === "discordou" ? "Marcado: IA errou" : "Feedback removido",
                status: "success", duration: 2000,
            });
            fetchImagens({ silencioso: true });
            if (previewImagem?.id === id) {
                setPreviewImagem(prev => prev ? { ...prev, ai_feedback_humano: feedback === "null" ? null : feedback } : prev);
            }
        } catch (e) {
            toast({ title: "Erro ao salvar feedback", status: "error", duration: 3000 });
        }
    };

    const filtrarPorIa = (status, gravidade = "") => {
        setFiltros(prev => ({ ...prev, ai_analise_status: status, ai_gravidade: gravidade }));
        setCurrentPage(1);
    };

    const handleFiltro = (campo, valor) => {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    };
    const limparFiltros = () => setFiltros(initialFiltros);

    const toggleSelecao = (id) => {
        setSelecionadas(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleSelecionarTodas = () => {
        if (selecionadas.size === imagens.length) setSelecionadas(new Set());
        else setSelecionadas(new Set(imagens.map(i => i.id)));
    };

    const abrirConfirm = (type, id = null, ids = null) => {
        setMotivoRejeicao("");
        setExcluirPermanente(false);
        setConfirmAlert({ isOpen: true, type, id, ids });
    };
    const fecharConfirm = () => setConfirmAlert({ isOpen: false, type: "", id: null, ids: null });

    const executarConfirm = async () => {
        setProcessing(true);
        try {
            const { type, id, ids } = confirmAlert;
            if (type === "aprovar")        { await aprovarImagem(id);        toast({ title: "Imagem aprovada", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "rejeitar")  { await rejeitarImagem(id, motivoRejeicao || null); toast({ title: "Imagem rejeitada", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "pendente")  { await marcarImagemPendente(id); toast({ title: "Imagem voltou para pendente", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "deletar")   {
                if (excluirPermanente) {
                    await excluirImagemPermanente(id);
                    toast({ title: "Imagem excluída permanentemente", status: "success", duration: 2000 });
                } else {
                    await deletarImagem(id);
                    toast({ title: "Imagem excluída", status: "success", duration: 2000 });
                }
                piscarItens([id]);
            }
            else if (type === "deletarPermanente") { await excluirImagemPermanente(id); toast({ title: "Imagem excluída permanentemente", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "restaurar") { await restaurarImagem(id);      toast({ title: "Imagem restaurada", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "principal") { await definirImagemPrincipal(id); toast({ title: "Definida como principal", status: "success", duration: 2000 }); piscarItens([id]); }
            else if (type === "aprovarMassa")  { const r = await aprovarImagensMassa(ids); toast({ title: `${r.data.atualizadas} imagens aprovadas`, status: "success", duration: 2500 }); piscarItens(ids); }
            else if (type === "rejeitarMassa") { const r = await rejeitarImagensMassa(ids, motivoRejeicao || null); toast({ title: `${r.data.rejeitadas} imagens rejeitadas`, status: "success", duration: 2500 }); piscarItens(ids); }
            else if (type === "excluirMassa")  {
                if (excluirPermanente) {
                    const r = await excluirImagensMassaPermanente(ids);
                    toast({ title: `${r.data.purgadas} imagens excluídas permanentemente`, status: "success", duration: 2500 });
                } else {
                    const r = await excluirImagensMassa(ids);
                    toast({ title: `${r.data.excluidas} imagens excluídas`, status: "success", duration: 2500 });
                }
                piscarItens(ids);
            }
            fecharConfirm();
            fetchImagens({ silencioso: true });
        } catch (e) {
            toast({
                title: "Erro",
                description: e.response?.data?.error || "Falha na operação",
                status: "error",
                duration: 4000,
            });
        }
        setProcessing(false);
    };

    const renderConfirmConteudo = () => {
        const { type, ids } = confirmAlert;
        const exigeMotivo = type === "rejeitar" || type === "rejeitarMassa";
        const labels = {
            aprovar: "Tem certeza que deseja aprovar esta imagem?",
            rejeitar: "Tem certeza que deseja rejeitar esta imagem?",
            pendente: "Voltar esta imagem para o status pendente? (limpa motivo de rejeição)",
            deletar: excluirPermanente
                ? "Excluir PERMANENTEMENTE? O arquivo físico será removido e não haverá restauração."
                : "Excluir esta imagem? (você poderá restaurar depois)",
            deletarPermanente: "Excluir PERMANENTEMENTE? O arquivo físico será removido e não haverá restauração.",
            restaurar: "Restaurar esta imagem?",
            principal: "Definir esta imagem como principal do produto?",
            aprovarMassa: `Aprovar ${ids?.length || 0} imagens selecionadas?`,
            rejeitarMassa: `Rejeitar ${ids?.length || 0} imagens selecionadas?`,
            excluirMassa: excluirPermanente
                ? `Excluir PERMANENTEMENTE ${ids?.length || 0} imagens? Arquivos físicos removidos, sem restauração.`
                : `Excluir ${ids?.length || 0} imagens selecionadas? (poderá restaurar depois)`,
        };
        const mostraCheckbox = type === "deletar" || type === "excluirMassa";
        return (
            <>
                <Text mb={(exigeMotivo || mostraCheckbox) ? 3 : 0} fontSize="sm"
                    color={excluirPermanente && mostraCheckbox ? "red.600" : undefined}>
                    {labels[type]}
                </Text>
                {mostraCheckbox && (
                    <Checkbox
                        isChecked={excluirPermanente}
                        onChange={(e) => setExcluirPermanente(e.target.checked)}
                        colorScheme="red" size="sm">
                        <Text fontSize="sm">Apagar permanentemente (sem possibilidade de restauração)</Text>
                    </Checkbox>
                )}
                {exigeMotivo && (
                    <Textarea
                        value={motivoRejeicao}
                        onChange={(e) => setMotivoRejeicao(e.target.value)}
                        placeholder="Motivo da rejeição (opcional)"
                        size="sm"
                    />
                )}
            </>
        );
    };

    const renderAcoes = (img, size = "xs") => (
        <HStack spacing={1} flexWrap="wrap">
            <Tooltip label="Visualizar" hasArrow>
                <IconButton size={size} icon={<ViewIcon />} aria-label="Ver"
                    onClick={() => setPreviewImagem(img)} />
            </Tooltip>
            {img.deleted_at ? (
                <>
                    {(() => { const d = diasParaPurga(img.deleted_at); return d !== null && (
                        <Text fontSize="9px" color={d <= 3 ? "red.600" : "orange.500"} whiteSpace="nowrap">
                            {d <= 0 ? "Remoção pendente" : `Remove em ${d}d`}
                        </Text>
                    ); })()}
                    <Tooltip label="Restaurar" hasArrow>
                        <IconButton size={size} colorScheme="blue" icon={<RepeatIcon />}
                            aria-label="Restaurar" onClick={() => abrirConfirm("restaurar", img.id)} />
                    </Tooltip>
                    <Tooltip label="Excluir definitivamente" hasArrow>
                        <IconButton size={size} colorScheme="red" icon={<DeleteIcon />}
                            aria-label="Excluir permanentemente"
                            onClick={() => abrirConfirm("deletarPermanente", img.id)} />
                    </Tooltip>
                </>
            ) : (
                <>
                    {img.status !== "aprovada" && (
                        <Tooltip label="Aprovar" hasArrow>
                            <IconButton size={size} colorScheme="green" icon={<CheckIcon />}
                                aria-label="Aprovar" onClick={() => abrirConfirm("aprovar", img.id)} />
                        </Tooltip>
                    )}
                    {img.status !== "rejeitada" && (
                        <Tooltip label="Rejeitar" hasArrow>
                            <IconButton size={size} colorScheme="red" icon={<CloseIcon />}
                                aria-label="Rejeitar" onClick={() => abrirConfirm("rejeitar", img.id)} />
                        </Tooltip>
                    )}
                    {img.status !== "pendente" && (
                        <Tooltip label="Marcar como pendente" hasArrow>
                            <IconButton size={size} colorScheme="orange" variant="outline" icon={<TimeIcon />}
                                aria-label="Pendente" onClick={() => abrirConfirm("pendente", img.id)} />
                        </Tooltip>
                    )}
                    {img.status === "aprovada" && !img.principal && (
                        <Tooltip label="Definir como principal" hasArrow>
                            <IconButton size={size} colorScheme="yellow" icon={<StarIcon />}
                                aria-label="Principal" onClick={() => abrirConfirm("principal", img.id)} />
                        </Tooltip>
                    )}
                    <Tooltip label="Analisar com IA" hasArrow>
                        <IconButton size={size} colorScheme="blue" variant="ghost"
                            aria-label="Analisar IA"
                            onClick={() => analisarIaIndividual(img.id)}
                            icon={<Text fontSize="xs">🤖</Text>} />
                    </Tooltip>
                    <Tooltip label="Procurar imagem correta (IA busca no Google e valida)" hasArrow>
                        <IconButton size={size} colorScheme="purple" variant="ghost"
                            aria-label="Auto-corrigir"
                            onClick={() => autoCorrigirImagem(img.id)}
                            icon={<Text fontSize="xs">🔎</Text>} />
                    </Tooltip>
                    <Tooltip label="Bloquear (impede reuso desta imagem)" hasArrow>
                        <IconButton size={size} colorScheme="red" variant="ghost" icon={<NotAllowedIcon />}
                            aria-label="Bloquear" onClick={() => abrirModalBloqueio(img)} />
                    </Tooltip>
                    <Tooltip label="Excluir" hasArrow>
                        <IconButton size={size} variant="ghost" icon={<DeleteIcon />}
                            aria-label="Excluir" onClick={() => abrirConfirm("deletar", img.id)} />
                    </Tooltip>
                </>
            )}
        </HStack>
    );

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            <Box maxW="1280px" mx="auto" py={{ base: 4, md: 6 }} px={{ base: 3, md: 8 }}>
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }}
                    mb={4} direction={{ base: "column", md: "row" }} gap={2}>
                    <Box>
                        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">Banco de Imagens</Text>
                        <Text fontSize="sm" color="gray.600">Moderação e gerenciamento de imagens dos produtos</Text>
                    </Box>
                    <HStack>
                        <ButtonGroup size="sm" isAttached variant="outline">
                            <Tooltip label="Visualização em tabela" hasArrow>
                                <IconButton icon={<ListIcon size={16} />} aria-label="Tabela"
                                    colorScheme={viewMode === "table" ? "purple" : "gray"}
                                    variant={viewMode === "table" ? "solid" : "outline"}
                                    onClick={() => setViewMode("table")} />
                            </Tooltip>
                            <Tooltip label="Visualização em cards" hasArrow>
                                <IconButton icon={<LayoutGrid size={16} />} aria-label="Cards"
                                    colorScheme={viewMode === "card" ? "purple" : "gray"}
                                    variant={viewMode === "card" ? "solid" : "outline"}
                                    onClick={() => setViewMode("card")} />
                            </Tooltip>
                        </ButtonGroup>
                        <Button leftIcon={<RepeatIcon />} onClick={fetchImagens} size="sm">Atualizar</Button>
                    </HStack>
                </Flex>

                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                    <Stat bg="white" p={3} borderRadius="md" boxShadow="sm" borderLeft="4px solid #ECC94B">
                        <StatLabel fontSize="xs">Pendentes</StatLabel>
                        <StatNumber fontSize={{ base: "lg", md: "xl" }}>{contadores.pendentes}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={3} borderRadius="md" boxShadow="sm" borderLeft="4px solid #38A169">
                        <StatLabel fontSize="xs">Aprovadas</StatLabel>
                        <StatNumber fontSize={{ base: "lg", md: "xl" }}>{contadores.aprovadas}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={3} borderRadius="md" boxShadow="sm" borderLeft="4px solid #E53E3E">
                        <StatLabel fontSize="xs">Rejeitadas</StatLabel>
                        <StatNumber fontSize={{ base: "lg", md: "xl" }}>{contadores.rejeitadas}</StatNumber>
                    </Stat>
                    <Stat bg="white" p={3} borderRadius="md" boxShadow="sm" borderLeft="4px solid #718096">
                        <StatLabel fontSize="xs">Excluídas</StatLabel>
                        <StatNumber fontSize={{ base: "lg", md: "xl" }}>{contadores.excluidas}</StatNumber>
                    </Stat>
                </SimpleGrid>

                <Box bg="white" p={3} borderRadius="md" boxShadow="sm" mb={4}>
                    <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                        <HStack>
                            <Text fontSize="sm" fontWeight="bold">🤖 Análise de IA</Text>
                            <Text fontSize="xs" color="gray.500">
                                ({contadoresIa.por_status.ok + contadoresIa.por_status.divergente + contadoresIa.por_status.erro} analisadas ·{" "}
                                {contadoresIa.por_status.nao_analisada} pendentes)
                            </Text>
                        </HStack>
                    </Flex>
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={2}>
                        <Stat as="button" textAlign="left" bg="red.50" p={2} borderRadius="md"
                            borderLeft="3px solid #E53E3E"
                            onClick={() => filtrarPorIa("divergente", "critico")} cursor="pointer">
                            <StatLabel fontSize="10px" color="red.700">Crítico</StatLabel>
                            <StatNumber fontSize="md" color="red.700">{contadoresIa.por_gravidade.critico}</StatNumber>
                        </Stat>
                        <Stat as="button" textAlign="left" bg="orange.50" p={2} borderRadius="md"
                            borderLeft="3px solid #DD6B20"
                            onClick={() => filtrarPorIa("divergente", "alto")} cursor="pointer">
                            <StatLabel fontSize="10px" color="orange.700">Alto</StatLabel>
                            <StatNumber fontSize="md" color="orange.700">{contadoresIa.por_gravidade.alto}</StatNumber>
                        </Stat>
                        <Stat as="button" textAlign="left" bg="yellow.50" p={2} borderRadius="md"
                            borderLeft="3px solid #D69E2E"
                            onClick={() => filtrarPorIa("divergente", "medio")} cursor="pointer">
                            <StatLabel fontSize="10px" color="yellow.700">Médio</StatLabel>
                            <StatNumber fontSize="md" color="yellow.700">{contadoresIa.por_gravidade.medio}</StatNumber>
                        </Stat>
                        <Stat as="button" textAlign="left" bg="gray.50" p={2} borderRadius="md"
                            borderLeft="3px solid #718096"
                            onClick={() => filtrarPorIa("divergente", "baixo")} cursor="pointer">
                            <StatLabel fontSize="10px" color="gray.700">Baixo</StatLabel>
                            <StatNumber fontSize="md" color="gray.700">{contadoresIa.por_gravidade.baixo}</StatNumber>
                        </Stat>
                        <Stat as="button" textAlign="left" bg="blue.50" p={2} borderRadius="md"
                            borderLeft="3px solid #3182CE"
                            onClick={() => filtrarPorIa("divergente", "info")} cursor="pointer">
                            <StatLabel fontSize="10px" color="blue.700">Info</StatLabel>
                            <StatNumber fontSize="md" color="blue.700">{contadoresIa.por_gravidade.info}</StatNumber>
                        </Stat>
                    </SimpleGrid>
                </Box>

                <Box bg="white" p={{ base: 3, md: 4 }} borderRadius="md" boxShadow="sm" mb={4}>
                    <FormControl mb={3}>
                        <FormLabel fontSize="xs" mb={1}>Buscar por ID do produto, nome, código global, código fornecedor, código fabricante ou marca</FormLabel>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input value={filtros.q}
                                onChange={(e) => handleFiltro("q", e.target.value)}
                                placeholder="Digite para buscar..." />
                        </InputGroup>
                    </FormControl>

                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Status</FormLabel>
                            <Select size="sm" value={filtros.status}
                                onChange={(e) => handleFiltro("status", e.target.value)}>
                                <option value="">Todos</option>
                                <option value="pendente">Pendente</option>
                                <option value="aprovada">Aprovada</option>
                                <option value="rejeitada">Rejeitada</option>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Origem</FormLabel>
                            <Select size="sm" value={filtros.origem}
                                onChange={(e) => handleFiltro("origem", e.target.value)}>
                                <option value="">Todas</option>
                                {Object.entries(ORIGEM_LABELS).map(([v, l]) => (
                                    <option key={v} value={v}>{l}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Fornecedor</FormLabel>
                            <Select size="sm" value={filtros.fornecedor_id}
                                onChange={(e) => handleFiltro("fornecedor_id", e.target.value)}>
                                <option value="">Todos</option>
                                {fornecedores.map(f => (
                                    <option key={f.id} value={f.id}>{f.nome}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Ordenar por</FormLabel>
                            <Select size="sm" value={filtros.sort}
                                onChange={(e) => handleFiltro("sort", e.target.value)}>
                                {SORT_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Análise da IA</FormLabel>
                            <Select size="sm" value={filtros.ai_analise_status}
                                onChange={(e) => handleFiltro("ai_analise_status", e.target.value)}>
                                <option value="">Todas</option>
                                <option value="nao_analisada">Não analisada</option>
                                <option value="analisando">Analisando</option>
                                <option value="ok">OK (IA não viu problema)</option>
                                <option value="divergente">Divergente</option>
                                <option value="erro">Erro</option>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Gravidade IA</FormLabel>
                            <Select size="sm" value={filtros.ai_gravidade}
                                onChange={(e) => handleFiltro("ai_gravidade", e.target.value)}>
                                <option value="">Todas</option>
                                {Object.entries(AI_GRAVIDADE_LABELS).map(([v, l]) => (
                                    <option key={v} value={v}>{l}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl display="flex" alignItems="flex-end" gridColumn={{ lg: "span 2" }}>
                            <HStack spacing={2} w="100%">
                                <Checkbox isChecked={filtros.incluir_excluidas}
                                    onChange={(e) => handleFiltro("incluir_excluidas", e.target.checked)}>
                                    <Text fontSize="xs">Incluir excluídas</Text>
                                </Checkbox>
                                <Button size="xs" variant="outline" onClick={limparFiltros}>Limpar</Button>
                            </HStack>
                        </FormControl>
                    </SimpleGrid>
                </Box>

                {selecionadas.size > 0 && (
                    <Flex bg="purple.50" p={3} borderRadius="md" mb={3}
                        align={{ base: "stretch", md: "center" }}
                        justify="space-between"
                        direction={{ base: "column", md: "row" }} gap={2}>
                        <Text fontSize="sm" fontWeight="medium">
                            {selecionadas.size} {selecionadas.size === 1 ? "imagem selecionada" : "imagens selecionadas"}
                        </Text>
                        <HStack flexWrap="wrap">
                            <Button size="sm" colorScheme="green" leftIcon={<CheckIcon />}
                                onClick={() => abrirConfirm("aprovarMassa", null, Array.from(selecionadas))}>
                                Aprovar
                            </Button>
                            <Button size="sm" colorScheme="red" leftIcon={<CloseIcon />}
                                onClick={() => abrirConfirm("rejeitarMassa", null, Array.from(selecionadas))}>
                                Rejeitar
                            </Button>
                            <Button size="sm" variant="outline" colorScheme="red" leftIcon={<DeleteIcon />}
                                onClick={() => abrirConfirm("excluirMassa", null, Array.from(selecionadas))}>
                                Excluir
                            </Button>
                            <Button size="sm" colorScheme="blue" variant="outline"
                                isLoading={analisandoIa}
                                onClick={analisarIaSelecionadas}>
                                🤖 Analisar com IA
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setSelecionadas(new Set())}>
                                Limpar
                            </Button>
                        </HStack>
                    </Flex>
                )}

                <Box bg="white" borderRadius="md" boxShadow="sm" overflow="hidden">
                    {loading ? (
                        <Flex justify="center" align="center" h="300px">
                            <Spinner size="xl" color="orange.500" />
                        </Flex>
                    ) : imagens.length === 0 ? (
                        <Flex justify="center" align="center" h="200px" px={4}>
                            <Text color="gray.500" textAlign="center">Nenhuma imagem encontrada com esses filtros.</Text>
                        </Flex>
                    ) : viewMode === "table" ? (
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple">
                                <Thead bg="gray.100">
                                    <Tr>
                                        <Th width="40px" px={2}>
                                            <Checkbox
                                                isChecked={selecionadas.size === imagens.length && imagens.length > 0}
                                                isIndeterminate={selecionadas.size > 0 && selecionadas.size < imagens.length}
                                                onChange={toggleSelecionarTodas} />
                                        </Th>
                                        <Th width="80px" px={2}>Imagem</Th>
                                        <Th>Produto</Th>
                                        <Th>Fornecedor</Th>
                                        <Th>Origem</Th>
                                        <Th>Status</Th>
                                        <Th>Análise IA</Th>
                                        <Th width="60px" textAlign="center">★</Th>
                                        <Th>Criado</Th>
                                        <Th>Ações</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {imagens.map((img) => (
                                        <Tr key={img.id}
                                            bg={destacarIds.has(img.id) ? "green.50" : (img.deleted_at ? "red.50" : "white")}
                                            transition="background 0.4s ease"
                                            _hover={{ bg: img.deleted_at ? "red.100" : "gray.50" }}>
                                            <Td px={2}>
                                                <Checkbox isChecked={selecionadas.has(img.id)}
                                                    onChange={() => toggleSelecao(img.id)} />
                                            </Td>
                                            <Td px={2}>
                                                <Image src={img.url} alt={`#${img.id}`}
                                                    boxSize="56px" objectFit="cover" borderRadius="md"
                                                    cursor="pointer" loading="lazy"
                                                    onClick={() => setPreviewImagem(img)}
                                                    fallbackSrc="https://via.placeholder.com/56?text=?" />
                                            </Td>
                                            <Td maxW="300px">
                                                <ProdutoInfo imagem={img} />
                                            </Td>
                                            <Td fontSize="xs">{img.produto_fornecedor_nome || "-"}</Td>
                                            <Td>
                                                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                                    {ORIGEM_LABELS[img.origem] || img.origem}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <Badge colorScheme={STATUS_COLORS[img.status]} fontSize="xs">
                                                    {img.status}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <AiBadge imagem={img} />
                                            </Td>
                                            <Td textAlign="center">
                                                {img.principal ? <StarIcon color="yellow.400" /> : ""}
                                            </Td>
                                            <Td fontSize="xs" whiteSpace="nowrap">
                                                {img.created_at ? new Date(img.created_at).toLocaleDateString("pt-BR") : "-"}
                                            </Td>
                                            <Td>{renderAcoes(img)}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    ) : (
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={2} p={3}>
                            {imagens.map((img) => (
                                <Card key={img.id} variant="outline"
                                    bg={img.deleted_at ? "red.50" : "white"}
                                    borderColor={destacarIds.has(img.id) ? "green.400" : (img.principal ? "yellow.400" : "gray.200")}
                                    boxShadow={destacarIds.has(img.id) ? "0 0 0 3px rgba(72,187,120,0.35)" : undefined}
                                    transition="all 0.4s ease">
                                    <Box position="relative" pb="75%" bg="gray.50" cursor="pointer"
                                        onClick={() => setPreviewImagem(img)}>
                                        <Image src={img.url} alt={`#${img.id}`}
                                            position="absolute" top={0} left={0}
                                            w="100%" h="100%" objectFit="contain"
                                            loading="lazy"
                                            fallbackSrc="https://via.placeholder.com/300?text=?" />
                                        <HStack position="absolute" top={1} left={1} spacing={1}>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <Checkbox isChecked={selecionadas.has(img.id)}
                                                    onChange={() => toggleSelecao(img.id)}
                                                    bg="white" borderRadius="sm" p={0.5} />
                                            </Box>
                                            {img.principal && (
                                                <Tag size="xs" colorScheme="yellow" px={1.5} py={0.5}>
                                                    <StarIcon boxSize={2.5} />
                                                </Tag>
                                            )}
                                        </HStack>
                                        <Badge colorScheme={STATUS_COLORS[img.status]}
                                            position="absolute" top={1} right={1} fontSize="9px">
                                            {img.status}
                                        </Badge>
                                        <Box position="absolute" bottom={1} left={1}>
                                            <AiBadge imagem={img} />
                                        </Box>
                                    </Box>
                                    <CardBody p={2}>
                                        <VStack align="stretch" spacing={1}>
                                            <ProdutoInfo imagem={img} compact />
                                            <Text fontSize="9px" color="gray.500" noOfLines={1}>
                                                {ORIGEM_LABELS[img.origem]} · #{img.id}
                                            </Text>
                                            {renderAcoes(img, "xs")}
                                        </VStack>
                                    </CardBody>
                                </Card>
                            ))}
                        </SimpleGrid>
                    )}
                </Box>

                {paginationMeta && paginationMeta.last_page > 1 && (
                    <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={2}>
                        <HStack>
                            <Text fontSize="sm" color="gray.600">
                                Página {paginationMeta.current_page} de {paginationMeta.last_page} · {paginationMeta.total} imagens
                            </Text>
                            <Select size="xs" maxW="100px" value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={60}>60 / pág</option>
                                <option value={120}>120 / pág</option>
                            </Select>
                        </HStack>
                        <HStack>
                            <Button size="sm" isDisabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
                            <Button size="sm" isDisabled={currentPage === paginationMeta.last_page}
                                onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
                        </HStack>
                    </Flex>
                )}
            </Box>

            <Modal isOpen={!!previewImagem} onClose={() => setPreviewImagem(null)}
                size={{ base: "full", md: "xl" }} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        Imagem #{previewImagem?.id}
                        {previewImagem?.produto_nome && ` — ${previewImagem.produto_nome}`}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {previewImagem && (
                            <>
                                <Flex justify="center" mb={4}>
                                    <Image src={previewImagem.url}
                                        maxH={{ base: "300px", md: "500px" }}
                                        objectFit="contain" />
                                </Flex>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                                    <Box><b>Produto:</b> {previewImagem.produto_nome || "-"}</Box>
                                    <Box><b>Código global:</b> {previewImagem.produto_codigo_global || "-"}</Box>
                                    <Box><b>Marca:</b> {previewImagem.produto_marca || "-"}</Box>
                                    <Box><b>Fornecedor:</b> {previewImagem.produto_fornecedor_nome || "-"}</Box>
                                    <Box><b>Cód. fornecedor:</b> {previewImagem.produto_codigo_fornecedor || "-"}</Box>
                                    <Box><b>Cód. fabricante:</b> {previewImagem.produto_codigo_fabricante || "-"}</Box>
                                    <Box><b>Status:</b> <Badge colorScheme={STATUS_COLORS[previewImagem.status]}>{previewImagem.status}</Badge></Box>
                                    <Box><b>Origem:</b> {ORIGEM_LABELS[previewImagem.origem]}</Box>
                                    <Box><b>Principal:</b> {previewImagem.principal ? "Sim" : "Não"}</Box>
                                    <Box><b>Dimensões:</b> {previewImagem.width}×{previewImagem.height}</Box>
                                    <Box><b>Tamanho:</b> {previewImagem.bytes ? `${Math.round(previewImagem.bytes / 1024)} KB` : "-"}</Box>
                                    <Box><b>Mime:</b> {previewImagem.mime_type}</Box>
                                    <Box gridColumn={{ md: "span 2" }} wordBreak="break-all">
                                        <b>URL:</b> <Text as="span" fontSize="xs">{previewImagem.url}</Text>
                                    </Box>
                                    {previewImagem.motivo_rejeicao && (
                                        <Box gridColumn={{ md: "span 2" }}>
                                            <b>Motivo rejeição:</b> {previewImagem.motivo_rejeicao}
                                        </Box>
                                    )}
                                </SimpleGrid>

                                <Box mt={4} p={3} borderRadius="md"
                                    bg={previewImagem.ai_analise_status === "divergente" ? "orange.50"
                                        : previewImagem.ai_analise_status === "ok" ? "green.50"
                                        : previewImagem.ai_analise_status === "erro" ? "red.50"
                                        : "gray.50"}
                                    borderLeft="4px solid"
                                    borderColor={previewImagem.ai_analise_status === "divergente" ? "orange.400"
                                        : previewImagem.ai_analise_status === "ok" ? "green.400"
                                        : previewImagem.ai_analise_status === "erro" ? "red.400"
                                        : "gray.300"}>
                                    <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                                        <Text fontWeight="bold" fontSize="sm">🤖 Análise de IA</Text>
                                        <AiBadge imagem={previewImagem} />
                                    </Flex>
                                    {previewImagem.ai_analise_status === "nao_analisada" ? (
                                        <Text fontSize="sm" color="gray.600">Esta imagem ainda não foi analisada pela IA.</Text>
                                    ) : previewImagem.ai_analise_status === "analisando" ? (
                                        <HStack><Spinner size="xs" /><Text fontSize="sm">Análise em andamento…</Text></HStack>
                                    ) : (
                                        <VStack align="stretch" spacing={1.5} fontSize="sm">
                                            {previewImagem.ai_tipo_problema && (
                                                <Box>
                                                    <b>Tipo de problema:</b>{" "}
                                                    {AI_TIPO_PROBLEMA_LABELS[previewImagem.ai_tipo_problema] || previewImagem.ai_tipo_problema}
                                                </Box>
                                            )}
                                            {previewImagem.ai_confianca != null && (
                                                <Box><b>Confiança:</b> {Math.round(previewImagem.ai_confianca)}%</Box>
                                            )}
                                            {previewImagem.ai_observacoes && (
                                                <Box><b>Observação:</b> {previewImagem.ai_observacoes}</Box>
                                            )}
                                            {previewImagem.ai_modelo && (
                                                <Text fontSize="xs" color="gray.500">
                                                    Modelo: {previewImagem.ai_modelo}
                                                    {previewImagem.ai_analisado_em && ` · ${new Date(previewImagem.ai_analisado_em).toLocaleString("pt-BR")}`}
                                                </Text>
                                            )}
                                        </VStack>
                                    )}
                                    {previewImagem.ai_analise_status === "divergente" && (
                                        <HStack mt={3} spacing={2} wrap="wrap">
                                            <Text fontSize="xs" color="gray.600">Seu julgamento:</Text>
                                            <Button size="xs" colorScheme={previewImagem.ai_feedback_humano === "concordou" ? "green" : "gray"}
                                                variant={previewImagem.ai_feedback_humano === "concordou" ? "solid" : "outline"}
                                                onClick={() => enviarFeedbackIa(previewImagem.id, "concordou")}>
                                                ✓ IA acertou
                                            </Button>
                                            <Button size="xs" colorScheme={previewImagem.ai_feedback_humano === "discordou" ? "red" : "gray"}
                                                variant={previewImagem.ai_feedback_humano === "discordou" ? "solid" : "outline"}
                                                onClick={() => enviarFeedbackIa(previewImagem.id, "discordou")}>
                                                ✗ IA errou
                                            </Button>
                                            {previewImagem.ai_feedback_humano && (
                                                <Button size="xs" variant="ghost"
                                                    onClick={() => enviarFeedbackIa(previewImagem.id, "null")}>
                                                    Limpar
                                                </Button>
                                            )}
                                        </HStack>
                                    )}
                                    <HStack mt={2} spacing={2} flexWrap="wrap">
                                        <Button size="xs" variant="outline"
                                            onClick={() => analisarIaIndividual(previewImagem.id)}>
                                            🔄 Reanalisar com IA
                                        </Button>
                                        <Button size="xs" colorScheme="purple" variant="outline"
                                            onClick={() => autoCorrigirImagem(previewImagem.id)}>
                                            🔎 Procurar imagem correta
                                        </Button>
                                    </HStack>
                                </Box>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button leftIcon={<DownloadIcon />} colorScheme="blue" variant="outline" mr={2}
                            onClick={() => downloadImagem(previewImagem?.url, previewImagem?.nome)}>
                            Baixar
                        </Button>
                        <Button onClick={() => setPreviewImagem(null)}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={modalBloqueio.isOpen} onClose={fecharModalBloqueio}
                size={{ base: "sm", md: "md" }} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Bloquear imagem #{modalBloqueio.imagemId}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3} align="stretch">
                            <Text fontSize="sm" color="gray.600">
                                A imagem bloqueada não poderá ser cadastrada novamente. Cópias existentes serão excluídas conforme o escopo.
                            </Text>
                            <FormControl>
                                <FormLabel fontSize="sm">Escopo</FormLabel>
                                <RadioGroup value={bloqueioForm.escopo}
                                    onChange={(v) => setBloqueioForm({ ...bloqueioForm, escopo: v })}>
                                    <ChakraStack direction="column" spacing={2}>
                                        <Radio value="produto" colorScheme="purple">
                                            <Text fontSize="sm">Apenas para o produto #{modalBloqueio.produtoIdImg}</Text>
                                        </Radio>
                                        <Radio value="global" colorScheme="red">
                                            <Text fontSize="sm">Globalmente (todos os produtos)</Text>
                                        </Radio>
                                    </ChakraStack>
                                </RadioGroup>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Motivo (opcional)</FormLabel>
                                <Textarea size="sm" rows={3}
                                    placeholder="Ex: imagem genérica, baixa qualidade..."
                                    value={bloqueioForm.motivo}
                                    onChange={(e) => setBloqueioForm({ ...bloqueioForm, motivo: e.target.value })} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={fecharModalBloqueio} isDisabled={bloqueando} size="sm">
                            Cancelar
                        </Button>
                        <Button colorScheme="red" onClick={confirmarBloqueio} isLoading={bloqueando} size="sm"
                            leftIcon={<NotAllowedIcon />}>
                            Bloquear
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertDialog isOpen={confirmAlert.isOpen} leastDestructiveRef={cancelRef}
                onClose={fecharConfirm} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Confirmar ação</AlertDialogHeader>
                        <AlertDialogBody>{renderConfirmConteudo()}</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={fecharConfirm} isDisabled={processing} size="sm">
                                Cancelar
                            </Button>
                            <Button colorScheme="purple" onClick={executarConfirm}
                                ml={3} isLoading={processing} size="sm">
                                Confirmar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default BancoImagens;
